import { supabase } from '../lib/supabase';

export const socialMediaService = {
  // Test social media platform connection
  async testConnection(platform, credentials) {
    try {
      // Simulate API connection test
      // In production, this would make actual API calls to verify credentials
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const testResults = {
        facebook: { success: true, message: 'Conexión exitosa a Facebook Graph API', apiVersion: 'v18.0' },
        instagram: { success: true, message: 'Conexión exitosa a Instagram Business API', accountType: 'Business' },
        whatsapp: { success: true, message: 'Conexión exitosa a WhatsApp Business API', phoneVerified: true },
        linkedin: { success: true, message: 'Conexión exitosa a LinkedIn Marketing API', organizationAccess: true }
      };

      return testResults?.[platform] || { success: false, message: 'Plataforma no soportada' };
    } catch (error) {
      console.error(`Error testing ${platform} connection:`, error);
      throw error;
    }
  },

  // Get all campaigns
  async getCampaigns(userId) {
    try {
      const { data, error } = await supabase
        ?.from('marketing_campaigns')
        ?.select('*')
        ?.eq('created_by', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(campaign => ({
        id: campaign?.id,
        name: campaign?.name,
        platform: campaign?.platform,
        status: campaign?.status,
        startDate: campaign?.start_date,
        endDate: campaign?.end_date,
        budget: campaign?.budget,
        targetAudience: campaign?.target_audience,
        content: campaign?.content,
        createdBy: campaign?.created_by,
        createdAt: campaign?.created_at,
        updatedAt: campaign?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  // Create new campaign
  async createCampaign(campaignData, userId) {
    try {
      const { data, error } = await supabase
        ?.from('marketing_campaigns')
        ?.insert({
          name: campaignData?.name,
          platform: campaignData?.platform,
          status: campaignData?.status || 'draft',
          start_date: campaignData?.startDate,
          end_date: campaignData?.endDate,
          budget: campaignData?.budget,
          target_audience: campaignData?.targetAudience,
          content: campaignData?.content,
          created_by: userId
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        platform: data?.platform,
        status: data?.status,
        startDate: data?.start_date,
        endDate: data?.end_date,
        budget: data?.budget,
        targetAudience: data?.target_audience,
        content: data?.content,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  // Get campaign analytics
  async getCampaignAnalytics(campaignId) {
    try {
      const { data, error } = await supabase
        ?.from('campaign_analytics')
        ?.select('*')
        ?.eq('campaign_id', campaignId)
        ?.order('recorded_at', { ascending: false })
        ?.limit(30);

      if (error) throw error;

      return data?.map(analytics => ({
        id: analytics?.id,
        campaignId: analytics?.campaign_id,
        impressions: analytics?.impressions,
        clicks: analytics?.clicks,
        conversions: analytics?.conversions,
        engagement: analytics?.engagement,
        reach: analytics?.reach,
        costPerLead: analytics?.cost_per_lead,
        recordedAt: analytics?.recorded_at
      }));
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
      throw error;
    }
  },

  // Get social media connections status
  async getConnectionsStatus(userId) {
    try {
      const { data, error } = await supabase
        ?.from('social_media_connections')
        ?.select('*')
        ?.eq('user_id', userId);

      if (error) throw error;

      return data?.map(connection => ({
        id: connection?.id,
        platform: connection?.platform,
        status: connection?.status,
        lastTested: connection?.last_tested,
        apiHealth: connection?.api_health,
        errorMessage: connection?.error_message,
        connectedAt: connection?.connected_at
      }));
    } catch (error) {
      console.error('Error fetching connections status:', error);
      throw error;
    }
  },

  // Update connection status
  async updateConnectionStatus(platform, userId, status, healthData) {
    try {
      const { data, error } = await supabase
        ?.from('social_media_connections')
        ?.upsert({
          user_id: userId,
          platform: platform,
          status: status,
          last_tested: new Date()?.toISOString(),
          api_health: healthData?.apiHealth || 'healthy',
          error_message: healthData?.errorMessage || null
        }, {
          onConflict: 'user_id,platform'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        platform: data?.platform,
        status: data?.status,
        lastTested: data?.last_tested,
        apiHealth: data?.api_health,
        errorMessage: data?.error_message
      };
    } catch (error) {
      console.error('Error updating connection status:', error);
      throw error;
    }
  },

  // Subscribe to campaign analytics updates
  subscribeToCampaignAnalytics(campaignId, callback) {
    const subscription = supabase
      ?.channel(`campaign_analytics_${campaignId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'campaign_analytics',
          filter: `campaign_id=eq.${campaignId}`
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return subscription;
  },

  // Unsubscribe from real-time updates
  unsubscribe(subscription) {
    if (subscription) {
      supabase?.removeChannel(subscription);
    }
  }
};

export default socialMediaService;
